import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import axios from "axios";
import { getDownloadURL, ref } from "firebase/storage";
import { storage } from "../tools/firebase";

const SupplierRetailerContext = createContext();

export const useSupplierRetailer = () => useContext(SupplierRetailerContext);

const ORG_KEY = "spearbi_org";
const DEFAULT_ORG = "SPEARMGMT";
// Master admins only — same hardcoded pair as AuthenticatedLayout/Sidebar.
const isMasterAdmin = () => {
  const userid = localStorage.getItem("userid");
  return userid === "1" || userid === "2";
};

export const SupplierRetailerProvider = ({ children }) => {
  const [selectedRetailer, setSelectedRetailer] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [organisations, setOrganisations] = useState([]);
  const [selectedOrg, setSelectedOrgState] = useState(() => localStorage.getItem(ORG_KEY) || DEFAULT_ORG);
  const [userOrg, setUserOrg] = useState(null); // caller's own org: { org_code, org_name } | null
  const [retailers, setRetailers] = useState([
    { value: "PNP", text: "PnP" },
    { value: "CHK", text: "Checkers" },
    { value: "MED", text: "MediRite" },
    { value: "PET", text: "Petshop" },
    { value: "DIS", text: "Dischem" },
    { value: "CLI", text: "Clicks" },
    { value: "MAK", text: "Makro" },
  ]);
  const [all_supplier_access, setAll_supplier_access] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableRetailers, setAvailableRetailers] = useState({});
  const [retailerLogos, setRetailerLogos] = useState({});

  // Master admins see the app scoped to one organisation at a time. Suppliers
  // without an org (rows carry `org` from the backend; null until phase 0 runs
  // on that DB) always stay visible, so this is a no-op on prod pre-migration.
  const user_supplier_access = useMemo(() => {
    if (!isMasterAdmin() || !selectedOrg) return all_supplier_access;
    return all_supplier_access.filter((s) => !s.org || s.org === selectedOrg);
  }, [all_supplier_access, selectedOrg]);

  const setSelectedOrg = (org_code) => {
    localStorage.setItem(ORG_KEY, org_code);
    setSelectedOrgState(org_code);
    // If the current supplier belongs to another org, jump to the first
    // supplier inside the newly selected one.
    const visible = all_supplier_access.filter((s) => !s.org || s.org === org_code);
    if (visible.length && !visible.some((s) => s.value === selectedSupplier)) {
      setSelectedSupplier(visible[0].value);
    }
  };

  const fetchOrganisations = async () => {
    if (!isMasterAdmin()) return;
    try {
      const userid = localStorage.getItem("userid");
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/get-organisations`, { userid });
      setOrganisations(response.data?.data || []);
    } catch (error) {
      // Pre-migration DBs have no organisations table — the toggle simply stays hidden.
      console.error("Error fetching organisations:", error);
    }
  };

  const fetchUserAccess = async (userid) => {
    try {
      setLoading(true);
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/login/get--user-access`, { userid });
      const { user_supplier_access, user_defaults, org } = response.data.data;

      setUserOrg(org || null);
      // The sidebar brand mark reads these synchronously (tools/brandMark.js).
      if (org && org.org_code) {
        localStorage.setItem("user_org_code", org.org_code);
        localStorage.setItem("user_org_name", org.org_name || org.org_code);
      } else {
        localStorage.removeItem("user_org_code");
        localStorage.removeItem("user_org_name");
      }
      setAll_supplier_access(user_supplier_access);
      // A master admin reloading while toggled into another org must not boot
      // into their default supplier if it sits outside that org — reconcile the
      // initial supplier against the persisted org scope.
      let initialSupplier = user_defaults.default_supplier;
      if (isMasterAdmin()) {
        const org = localStorage.getItem(ORG_KEY) || DEFAULT_ORG;
        const visible = user_supplier_access.filter((s) => !s.org || s.org === org);
        if (visible.length && !visible.some((s) => s.value === initialSupplier)) {
          initialSupplier = visible[0].value;
        }
      }
      setSelectedSupplier(initialSupplier);
      setSelectedRetailer(user_defaults.default_retailer);
      fetchOrganisations();
    } catch (error) {
      console.error("Error fetching user access:", error);
    } finally {
      setLoading(false);
    }
  };
  // Re-pull the supplier LIST only — unlike fetchUserAccess this never touches
  // the current supplier/retailer selection or the loading flag, so it is safe
  // to call mid-session after an admin action changes which suppliers exist
  // (creating or deleting a supplier). Silent on failure: the list simply
  // stays stale until the next full app load.
  const refreshSupplierList = async () => {
    try {
      const userid = localStorage.getItem("userid");
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/login/get--user-access`, { userid });
      setAll_supplier_access(response.data.data.user_supplier_access);
    } catch (error) {
      console.error("Error refreshing supplier list:", error);
    }
  };
  const fetchSupplierAccess = async () => {
    try {
      const userid=localStorage.getItem("userid");
      setLoading(true);
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/get-supplier-access`, { supplier: selectedSupplier,userid });
      setAvailableRetailers(response.data.data);
      const firstAvailableRetailer = Object.keys(response.data.data).find((key) => response.data.data[key]);
      if (firstAvailableRetailer) {
        setSelectedRetailer(firstAvailableRetailer);
      }
    } catch (error) {
      console.error("Error fetching user access:", error);
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   const fetchLogos = async () => {
  //     const logoUrls = {};
  //     const retailerValues = ["PNP", "CHK", "DIS", "CLI", "MAK"];
  //     for (const value of retailerValues) {
  //       const logoRef = ref(storage, `retailers/${value.toLowerCase()}.jpg`);
  //       try {
  //         const url = await getDownloadURL(logoRef);
  //         logoUrls[value] = url;
  //       } catch (error) {
  //         console.error("Error fetching logo:", error);
  //         // Optionally use a default logo if there is an error fetching the retailer logo
  //       }
  //     }
  //     setRetailerLogos(logoUrls);
  //   };

  //   fetchLogos();
  // }, []);

  const resetContext = () => {
    setAll_supplier_access([]);
    setOrganisations([]);
    setUserOrg(null);
    setSelectedRetailer("");
    setSelectedSupplier("");
    setLoading(false);
    setAvailableRetailers({});
    // setRetailerLogos({});
  };

  useEffect(() => {
    setAvailableRetailers({});
    fetchSupplierAccess();
  }, [selectedSupplier]);

  return (
    <SupplierRetailerContext.Provider
      value={{
        selectedRetailer,
        setSelectedRetailer,
        selectedSupplier,
        setSelectedSupplier,
        retailers,
        user_supplier_access,
        loading,
        fetchUserAccess,
        resetContext,
        availableRetailers,
        fetchSupplierAccess,
        refreshSupplierList,
        organisations,
        selectedOrg,
        setSelectedOrg,
        fetchOrganisations,
        userOrg,
        isMaster: isMasterAdmin()
        // retailerLogos,
      }}
    >
      {children}
    </SupplierRetailerContext.Provider>
  );
};
